import {
  ChangeDetectorRef, ChangeDetectionStrategy, Component,
  ViewChild, NgZone, OnInit, OnDestroy,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { Subscription, Observable } from 'rxjs';
import {filter, takeWhile} from 'rxjs/operators';

import * as _ from 'lodash';

import { indexPagesData } from 'utils/index-pages-data';
import {
  advancedSearchRouteMap, getFilterPostObject
} from 'shared/search/search-helpers';
import { columns, availableColumns } from './search/search-results-columns/index';
import { Config } from 'shared/config/env.config';
import { SIMPLE_CONFIRM_MODAL_NAME } from 'shared/modal/modal-names';
import { showAnimation } from 'shared/search-page/animations';
import { SearchModel } from 'shared/search/search.model';
import { ReturnRouteModel } from 'shared/return-route/return-route.model';
import { ActiveFilterModel } from 'shared/search/active-filters/active-filter.model';
import { SIMPLE_FILTER_TYPE } from 'shared/search/active-filters/active-filter-types';
import { PACT } from './components/contextual-actions/pim-config';
import { actions } from './my-products-actions';
import { QueueProcess } from 'shared/process/queue-process.model';
import { exportScheduledSuccessfullyToast } from 'shared/toaster/toasts';
import { ColumnPreset } from 'shared/column-builder/column-preset.interface';

import { CreateNewProductService } from 'shared/modal/create-new-product-modal/create-new-product.service';
import { AuthService } from 'shared/auth/auth.service';
import { AppRoutingService } from '../app-routing.service';
import { BrandService } from 'shared/brand/brand.service';
import { ProcessService } from 'shared/process/process.service';
import { ScrollToService } from 'shared/base/scroll-to.service';
import { ListManagerService } from 'shared/list-manager/list-manager.service';
import { PermissionsService } from 'shared/permissions/permissions.service';
import { ReturnRouteService } from 'shared/return-route/return-route.service';
import { SavedSearchesService } from 'shared/saved-searches/saved-searches.service';
import { UserService } from 'shared/user/user.service';
import { AppModalService } from 'shared/modal/modal.service';
import { MyProductsStoreDataService } from 'shared/my-products-store-data/my-products-store-data.service';
import { SearchPageService } from 'shared/search-page/search-page.service';
import { FitmentsService } from 'shared/fitments/fitments.service';
import { PreferencesService } from 'shared/preferences/preferences.service';
import { SubscriptionService } from 'shared/subscription/subscription.service';
import { ScorecardService } from 'shared/scorecard/scorecard.service';
import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';
import { MyProductsSearchService } from './search/my-products-search.service';
import { AlertsService } from 'shared/alerts/alerts.service';

import { SearchPageComponent } from 'shared/search-page/search-page.component';
import { SimpleConfirmModalComponent } from 'shared/modal/simple-confirm-modal/simple-confirm-modal.component';
import { SubscriptionWidgetComponent } from 'shared/subscription/subscription-widget/subscription-widget.component';
import { ProductPanelComponent } from '../product-panel/product-panel.component';
import { MyProductsHeaderComponent } from './components/my-products-header/my-products-header.component';
/* eslint-disable */
import { ConfigureExportWidgetComponent } from './components/configure-export-widget/configure-export-widget/configure-export-widget.component';
/* eslint-enable */
import { ViewArchivedProductsModalComponent } from './components/view-archived-products-modal/view-archived-products-modal.component';

import { CreateProductCopyModalComponent } from 'shared/modal/create-product-copy-modal/create-product-copy-modal.component';
import { ProductCopyType } from 'shared/modal/create-product-copy-modal/product-copy-type';
import Bugsnag from '@bugsnag/js';
import { Alert } from 'shared/alerts/alerts.model';

@Component({
  moduleId: module.id,
  selector: 'app-my-products',
  templateUrl: 'my-products.component.html',
  styleUrls: ['my-products.component.scss'],
  providers: [
    {provide: 'LIST_TYPE', useValue: 'item'},
    ListManagerService,
    CreateNewProductService,
    ScorecardConnectorService,
  ],
  animations: [showAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyProductsComponent extends SearchPageComponent implements OnInit, OnDestroy {
  @ViewChild(ProductPanelComponent, {static: false}) productPanel: ProductPanelComponent | void;

  // @ViewChild(ProductAdvancedSearchComponent, {static: false})
  // advancedSearch: ProductAdvancedSearchComponent;

  @ViewChild(ConfigureExportWidgetComponent, {static: false}) configureExportWidget: ConfigureExportWidgetComponent | void;

  @ViewChild('archiveModal', {static: false})
  archiveModal: SimpleConfirmModalComponent | void;

  @ViewChild('copyProductDataModal', {static: false})
  copyProductDataModal: CreateProductCopyModalComponent | void;

  @ViewChild('publishWarningModal', {static: false})
  publishWarningModal: SimpleConfirmModalComponent | void;

  @ViewChild(ViewArchivedProductsModalComponent, {static: false})
  viewArchivedProductsModal: ViewArchivedProductsModalComponent | void;

  @ViewChild(SubscriptionWidgetComponent, {static: false})
  subscrWidget: SubscriptionWidgetComponent | void;

  @ViewChild(MyProductsHeaderComponent, {static: false})
  declare headerEl: MyProductsHeaderComponent | void;

  pagesData: any = indexPagesData;
  pagesMenuData: any = {};
  contentSelector: string = '#my-products-content';
  detailsPageRootRoute: string = 'product-details-v2';
  ghostValue: string = 'index';
  maxColumnsInSearch: number = 12;
  notRemovableColumns: Array<string> = ['partNumber'];
  actions: any = actions;
  scorecardList: Array<any> = [];
  hasScorecards: boolean = true;
  selectedScorecardId: string;

  checkImportCompleteTimeout: any;
  importComplete: boolean;
  importReviewRoutes: Array<string> = ['import-review-aces', 'import-review-pies', 'import-review-custom-fields'];

  timezone: any;
  importStatusChangeNote: string = 'New data will overwrite existing data. Are you sure you want to&nbsp;continue?';
  /* eslint-disable */
  customFieldsStatusChangeNote: string = 'New data will overwrite existing data within the custom segment only. Are you sure you want to&nbsp;continue.';
  /* eslint-enable */
  saveSuffix: string;
  showSidedrawer: boolean = true; // use this field to show sidedrawer
  fileName: string; // for import/export review file

  columnPresets: Array<ColumnPreset> = [
    {
      title: 'Segment scores',
      name: 'segmentScores',
      columnNames: [
        'photo',
        'partNumber',
        'detail_score',
        'description_score',
        'fitment_score',
        'pricing_score',
        'digital_assets_score',
        'attributes_score',
        'packaging_score',
        'kits_score',
        'interchange_score',
        'score',
      ]
    }
  ];

  private routerChangeSub: Subscription;
  private routesToDetailsPageFitments: Array<string> = ['view-export', 'published'];

  createCopyType: ProductCopyType = ProductCopyType.CreateCopy;
  copyDataType: ProductCopyType = ProductCopyType.CopyData;

  constructor(
    public searchService: MyProductsSearchService,
    protected router: Router,
    protected brandService: BrandService,
    protected pageService: SearchPageService,
    protected processService: ProcessService,
    protected scrollToService: ScrollToService,
    protected listsService: ListManagerService,
    protected appRoutingService: AppRoutingService,
    protected permissionsService: PermissionsService,
    protected ngZone: NgZone,
    protected ref: ChangeDetectorRef,
    protected route: ActivatedRoute,
    protected returnRouteService: ReturnRouteService,
    protected location: Location,
    protected savedSearchesService: SavedSearchesService,
    protected userService: UserService,
    private myProductsStoreDataService: MyProductsStoreDataService,
    private authService: AuthService,
    private fitmentsService: FitmentsService,
    private preferencesService: PreferencesService,
    private subscriptionService: SubscriptionService,
    private appModalService: AppModalService,
    private createNewProductService: CreateNewProductService,
    private scorecardService: ScorecardService,
    private toastrService: ToastrService,
    private alertsService: AlertsService,
  ) {
    super(
      searchService, brandService, pageService, processService, scrollToService,
      appRoutingService, listsService, permissionsService, ngZone, ref, route,
      location, savedSearchesService, router, userService,
    );
  }

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  get brandId(): string {
    return this.brandService.getBrand();
  }

  get searchInputValue(): string {
    // handle it in the components
    // to get search value from header search input
    return this.headerEl && this.headerEl.searchInputValue;
  }

  get isWorkingPublishedPage() {
    return this.page === 'working' || this.page === 'published';
  }

  get isTabsClickableWhenActive() {
    return !this.isWorkingPublishedPage;
  }

  get rootUrl(): string {
    // my-products, import-manager, export-manager
    return this.route.snapshot.url[0].path;
  }

  get page(): string {
    return this.route.snapshot.params['page'];
  }

  get leftTabTitle(): string {
    return this.isWorkingPublishedPage ? 'Working' : 'Export Manager';
  }

  get rightTabTitle(): string {
    return this.isWorkingPublishedPage ? 'Published' : 'Import Manager';
  }

  get isLeftTabActive(): boolean {
    if (this.isWorkingPublishedPage) {
      return this.page === 'working';
    } else {
      return this.page.indexOf('view-export') >= 0;
    }
  }

  get isLeftTabClickable(): boolean {
    if (this.isWorkingPublishedPage) {
      return this.permissionsService.hasPermission(['admin', 'catalog-edit', 'fitments']);
    } else {
      return true;
    }
  }

  get isRightTabActive(): boolean {
    if (this.isWorkingPublishedPage) {
      return this.page === 'published';
    } else {
      return this.page.indexOf('import-review') >= 0;
    }
  }

  get isRightTabClickable(): boolean {
    if (this.isWorkingPublishedPage) {
      return this.permissionsService.hasPermission(['admin', 'catalog-edit']);
    } else {
      return this.permissionsService.hasPermission(['admin', 'catalog-edit']);
    }
  }

  get currentRoute(): string {
    return this.searchModel && this.searchModel.route || '';
  }

  get rowStatusHidden(): boolean {
    return this.currentRoute === 'view-export' ||
    this.currentRoute === 'published';
  }

  get headerColor(): string {
    return this.pagesData[this.currentRoute].headerColor;
  }

  get selectedRowsIds(): Array<any> {
    return _.map(this.selectedRows, 'partNumber');
  }

  get advancedSearchStatus(): string {
    return advancedSearchRouteMap[this.currentRoute];
  }

  get hasAdvancedSearch(): boolean {
    return _.has(advancedSearchRouteMap, this.currentRoute);
  }

  get storedSearchModel(): SearchModel | null {
    if (_.has(this, 'user.id')) {
      return JSON.parse(
        localStorage.getItem(`myProductsSearchModel.${this.saveSuffix}`)
      );
    } else {
      return null;
    }
  }

  get isImportPage(): boolean {
    return this.searchModel && this.isImportReviewRoute(this.searchModel.route) || false;
  }

  get isAdmin(): boolean {
    return this.permissionsService.hasPermission(['admin']);
  }

  get statusChangeTitle(): string {
    if (this.isImportPage) {
      if (this.currentRoute === 'import-review-custom-fields') {
        return 'Confirm Import of Custom Fields';
      }
      return 'Confirm Import to Catalog';
    }
    return 'Confirm Status Changes';
  }

  get statusChangeNote(): string {
    if (this.isImportPage) {
      if (this.currentRoute === 'import-review-custom-fields') {
        return this.customFieldsStatusChangeNote;
      }
      return this.importStatusChangeNote;
    }
    return null;
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  ngOnInit() {
      // this.listenToSocket();
      /*
       * Users with only the permission 'catalog-view' will only be able
       * to see 'published'. If the user has ONLY this permission
       * and does not have `admin` or `catalog-edit` redirect the user
       * to `published` route.
       */

       if (this.rootUrl === 'import-manager' || this.rootUrl === 'export-manager') {
         this.hasScorecards = false;
         this.scorecardList = [];
       }

       // get timezone
       this.preferencesService.getPreferences()
       .subscribe((preferences: any) => {
         this.timezone = preferences.timezone;
       });

    const selectedScorecard = _.get(this.route.snapshot.params, 'selectedScorecard');
    if (selectedScorecard) {
      this.scorecardService.setSelectedScorecardId(selectedScorecard);
      this.selectedScorecardId = selectedScorecard;
    }

    super.ngOnInit();
    this.handleRouterChange();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.clearCheckImportCompleteTimeout();
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  initPage() {
    this.searchModel.route = this.page;
    this.pagesMenuData = this.applyPermissions(this.myProductsStoreDataService.pagesMenuData);
  }

  updateSaveSuffix() {
    // {:page}.{:searchId?}.{:brandId}.{:userId}
    if (_.has(this, 'user.id')) {
      this.saveSuffix = this.page;
      const searchId = this.route.snapshot.params['searchId'];
      if (searchId) {
        this.saveSuffix = `${this.saveSuffix}.${searchId}`;
      }
      this.saveSuffix = `${this.saveSuffix}.${this.brandId}.${this.user.id}`;
    }
  }

  applyPermissions(pagesMenuData: any) {
    return _.map(pagesMenuData, (menuItem: any) => {
      if (menuItem.allow) {
          menuItem.visible = this.permissionsService.hasPermission(menuItem.allow);
          return menuItem;
      }
    });
  }

  initSubscriptions() {
    super.initSubscriptions();
    this.routerChangeSub = this.subscribeRouterChange();
  }

  destroySubscriptions() {
    super.destroySubscriptions();
    if (this.routerChangeSub) {
      this.routerChangeSub.unsubscribe();
    }
  }

  // replace parent method:
  addToListRequest(selectedLists: any): Observable<any> {
    let _filter: any = null;
    let state: any = null;
    let status: any = null;

    if (this.selectAll === 'in-list') {
       this.searchModel.searchId = this.getSearchId();
        _filter = this.searchService.getQueryString(this.searchModel, true);
       status = _.get(this.searchModel, 'statusModel.id', '');
       state = this.searchService.getListFilterState();
    }

    return this.listsService.addToDataLists(
      this.selectedRowsIds,
      selectedLists,
      status,
      state,
      _filter,
    );
  }

  storeSearchModel(searchModel: SearchModel) {
     if (_.has(this, 'user.id')) {
       localStorage.setItem(
         `myProductsSearchModel.${this.saveSuffix}`,
         JSON.stringify(searchModel)
       );
     }
   }

  setPagesMenuItemVisibility(menuName: string, visibility: boolean) {
    const menuItem: any = _.find(this.pagesMenuData, {name: menuName});
    if (menuItem) {
      menuItem.visible = visibility;
      this.myProductsStoreDataService.setMenuData(this.pagesMenuData);
    }
  }

  runSearch(searchModel: SearchModel, setColumn: boolean = true) {
    this.startLoading();
    searchModel.searchId = this.getSearchId();

    if (setColumn) {
        this.searchModel.columns = this.getRequestedColumns(null);
    }

    this.searchService.runSearch(this.brandId, searchModel)
    .subscribe(searchResults => {
      if (_.has(this.searchModel, 'statusModel.id')) {
        // @ts-ignore
        this.searchModel.statusModel = _.find(this.statuses, {id: this.searchModel.statusModel.id});
      }
      if (searchResults.user_filename) {
        this.fileName = searchResults.user_filename; // export
      }
      if (searchResults.filename) {
        this.fileName = searchResults.filename; // import
      }
    }, (err) => {
      super.handleSearchError();
    }, () => this.stopLoading());
  }

  getRequestedColumns(selectedColumns: any) {
      if (selectedColumns) {
          return _.map(selectedColumns, (resultColumns) => {
              if (resultColumns.field_name) {
                  return resultColumns.field_name;
              } else {
                  return (resultColumns.column_name) ? resultColumns.column_name : resultColumns.columnName;
              }
          });
      } else {
          if (this.selectedColumns) {
              return _.map(this.selectedColumns, (resultColumns) => {
                  return (resultColumns.field_name) ? resultColumns.field_name : resultColumns.column_name;
              });
          } else {
              return _.map(this.searchResultsColumns, (resultColumns) => {
                  return (resultColumns.field_name) ? resultColumns.field_name : resultColumns.columnName;
              });
          }
      }
  }

  onColumnsSave(selectedColumns: Array<any>) {
      this.searchModel.columns = this.getRequestedColumns(selectedColumns);
      this.runSearch(this.searchModel, false);
      super.onColumnsSave(selectedColumns);
  }

  handleAction(action: any) {
    this.startLoading();
    if (action.type === 'import-items') {
      if (this.allSelected) {
        let _filter: any = {};
        _filter.searchValue = this.searchModel.searchValue;
        _filter.status = (this.searchModel.statusModel) ? this.searchModel.statusModel.id.toString() : '';
        _filter.searchId = (this.searchModel.searchId) ? this.searchModel.searchId.toString() : '';
        _filter.list = this.searchService.listFilterId;
        _filter = _.assign(_filter, getFilterPostObject(this.searchModel.filters));

        this.processService.postImportReviewItems(this.brandId, [], _filter)
          .subscribe(
            response => {
              this.stopLoading();
              if (!response.success && response.message === 'Exceeds plan item limit') {
                if (this.subscrWidget) {
                  this.subscrWidget.openPlanWarningProductsModal();
                }
              } else if (response.processId) {
                this.stopLoading();
                const process: QueueProcess = new QueueProcess(response.processId, 0);
                this.activeQueueProcesses.push(process);
                if (this.headerEl) {
                  this.headerEl.onShowNotifications();
                }
                this.detectQueueProcessProgress(process);
              }
            },
            error => { this.stopLoading(); } );
      } else {
        this.processService.postImportReviewItems(this.brandId, _.map(this.selectedRows, 'itemId'))
          .subscribe(
            response => {
              this.stopLoading();
              if (!response.success && response.message === 'Exceeds plan item limit') {
                if (this.subscrWidget) {
                  this.subscrWidget.openPlanWarningProductsModal();
                }
              } else if (response.processId) {
                this.stopLoading();
                const process: QueueProcess = new QueueProcess(response.processId, 0);
                this.activeQueueProcesses.push(process);
                if (this.headerEl) {
                  this.headerEl.onShowNotifications();
                }
                this.detectQueueProcessProgress(process);
              }
            },
            error => { this.stopLoading(); } );
      }
    } else if (action.type === 'import-apps') {
      let request: any;
      if (this.allSelected) {
        let _filter: any = {};
        _filter.searchValue = this.searchModel.searchValue;
        _filter.status = (this.searchModel.statusModel) ? this.searchModel.statusModel.id.toString() : '';
        _filter.searchId = (this.searchModel.searchId) ? this.searchModel.searchId.toString() : '';
        _filter.list = this.searchService.listFilterId;
        _filter = _.assign(_filter, getFilterPostObject(this.searchModel.filters));

        request = this.processService.postImportReviewApps(this.brandId, [], _filter);
      } else {
        request = this.processService.postImportReviewApps(this.brandId, _.map(this.selectedRows, 'itemId'));
      }

      request.subscribe(
        response => {
          this.stopLoading();
          if (response.processId) {
            this.stopLoading();
            const process: QueueProcess = new QueueProcess(response.processId, 0);
            this.activeQueueProcesses.push(process);
            if (this.headerEl) {
              this.headerEl.onShowNotifications();
            }
            this.detectQueueProcessProgress(process);
          }
        },
        error => { this.stopLoading(); } );

    } else if (action.type === 'update-status' || action.type === 'archive') {
      this.handleUpdateStatus(
        this.allSelected,
        this.selectedRows,
        this.totalRecords,
        this.searchModel,
        action,
        null
      );
    }
  }

  handleBulkEdit() {
    if (this.productPanel) {
      this.productPanel.toggle(true, 'item-details');
    }
  }

  handleViewDetails() {
    this.navigateToDetailsPage(_.head(this.selectedRows));
  }

  listenToSocket() {
    if (this.authService.token() === null) {
      this.authService.logout();
    }
  }

  storeColumns(columnList: Array<any>) {
    if (_.has(this, 'user.id')) {
      localStorage.setItem(
        `myProductsColumns.${this.saveSuffix}`,
        JSON.stringify(columnList)
      );
    }
  }

  changeColumns(selectedColumns: Array<any>): void {
    super.changeColumns(selectedColumns);
    // this.rewriteUrl(this.searchModel, this.searchResultsColumns);
  }

  // inherited method of search-page component
  handlePageAction(action: string) {
    switch (action) {
      case 'add-product':
        if (this.createNewProductModal) {
          this.createNewProductModal.show();
        }
        break;
      case 'add-product-from-current':
        if (this.createProductCopyModal) {
          this.createProductCopyModal.show();
        }
        break;
      case 'view-archived':
        if (this.viewArchivedProductsModal) {
          this.viewArchivedProductsModal.show();
        }
        break;
      default:
        break;
    }
  }

  getDefaultColumns(route?: string): any {
    return _.cloneDeep(columns[route || this.currentRoute]);
  }

  // inherited method of search-page component
  handleSearchModelBeforeSearch() {
    if (this.hasScorecards) {
      this.scorecardService.getScorecards(this.brandId)
        .subscribe(scorecards => {
          this.scorecardList = scorecards;
          this.selectedScorecardId = this.scorecardService.getSelectedScorecardId();

          if (this.selectedScorecardId !== 'off') {
            // @ts-ignore
              const selectedScorecard = _.find(this.scorecardList, {_id: this.selectedScorecardId});
            if (selectedScorecard) {
              this.setScorecardFilter(selectedScorecard);
            } else {
              this.clearScorecardFilter();
            }
          } else {
            this.clearScorecardFilter();
          }
          this.ref.markForCheck();
          _.defer(() => this.headerEl && this.headerEl.selectScorecardById(this.selectedScorecardId));
          this.handleSearchModelBeforeSearchSubj.next(true);
        });
    } else {
      this.handleSearchModelBeforeSearchSubj.next(true);
    }
  }

  /*******************************************
   * COMPONENT EVENT HANDLERS
   *******************************************/

  onRemoveMenuItem(menuItem: any) {
    this.setPagesMenuItemVisibility(menuItem.name, false);
  }

  onFiltersAmountClick() {
    this.pageService.expandHeader(true);
  }

  onDetailsBtnClick(_id: string) {
    // @ts-ignore
      const item = _.find(this.searchResults, {_id});
    this.navigateToDetailsPage(item);
  }

  onCreateNewProductClicked(data: any) {
    this.createNewProductService
    .post(this.brandId, data)
    .subscribe((res: any) => {
      if (res.message) {
        // error while add products
        if (this.subscrWidget) {
          this.subscrWidget.openPlanWarningProductsModal();
        }
      } else {
        this.subscriptionService.clearStoredSubscription();
        this.runSearch(this.searchModel);
      }
    });
  }

  onCreateProductCopyClicked(res: any) {
    if (!res.processId) {
      this.runSearch(this.searchModel);
    } else {
      this.stopLoading();
      const process: QueueProcess = new QueueProcess(res.processId, 0);
      this.activeQueueProcesses.push(process);
      if (this.headerEl) {
        this.headerEl.onShowNotifications();
      }
      this.detectQueueProcessProgress(process);
    }
  }

  onCopyProductDataClicked(res: any) {
    this.searchService.deselectAll();
    if (!res.processId) {
      this.runSearch(this.searchModel);
    } else {
      this.stopLoading();
      const process: QueueProcess = new QueueProcess(res.processId, 0);
      this.activeQueueProcesses.push(process);
      if (this.headerEl) {
        this.headerEl.onShowNotifications();
      }
      this.detectQueueProcessProgress(process);
    }
  }

  onAdvancedSearchRun() {
    this.searchModel.currentPage = 1;
    super.onAdvancedSearchRun();
  }

  onLinkClick($event: any) {
    const item = _.find(this.searchResults, {_id: $event.row._id});
    if (_.includes(['partNumber', 'photo'], $event.column.col)) {
      this.navigateToDetailsPage(item);
    } else if ($event.column.col === 'fitments') {
      this.navigateToDetailsPage(item, 'fitments');
    }
  }

  onPublishedConfigureExport() {
    if (this.configureExportWidget) {
      this.configureExportWidget.configureExport();
    }
  }

  onConfigureExportFinished(exportConfig: any) {
    this.exportItems(exportConfig, false);
  }

  onConfigureExportReceiverFinished(exportConfig: any) {
    this.exportItems(exportConfig, true);
  }

  onConfigureExportScheduleFinished(exportConfig: any) {
    const hasReceivers = _.has(exportConfig, 'receivers') && !!exportConfig.receivers.length;
    this.exportItems(exportConfig, hasReceivers);
  }

  onFinalExportModalClosed() {
    this.searchModel.selectAll = 'on-page';
    this.searchService.deselectAll();
  }

  onActionSelected(action: any) {
    if (action.type === 'export-selected') {
      if (this.selectedRows.length || this.allSelected) {
        if (action.id === PACT.EXPORT_SELECTED.id) {
          if (this.configureExportWidget) {
            this.configureExportWidget.configureExport();
          }
        } else if (action.id === PACT.EXPORT_SELECTED_TO_RECEIVER.id) {
          const itemIds  = _.map(this.searchService.selectedItems, this.searchService.idField);
          this.postExportItemsValid();
        }
      } else {
        this.appModalService.showModal(
          SIMPLE_CONFIRM_MODAL_NAME,
          {
            secondaryBtnTitle: 'OK',
            note: 'In order to Export Selected items you must  make a selection. ' +
                  'Select by clicking the checkboxes on the left.',
            title: 'Please make a selection',
            version2: true,
          },
        );
      }
    } else if (action.type === 'archive') {
      this.actionData = action;
      if (this.archiveModal) {
        this.archiveModal.show();
      }
    } else if (action.type === 'copy-selected') {
      this.actionData = action;
      if (this.copyProductDataModal) {
        this.copyProductDataModal.show();
      }
    } else {
        if (action.type === 'update-status' && action.action === 'PUBLISH') {
            this.postItemsValid(action);
        } else {
            super.onActionSelected(action);
        }
    }
  }

  onProductsDelete() {
    console.log('delete products request');
  }

  onProductsArchive() {
    this.handleAction(this.actionData);
  }

  onProductsUnArchive(params: any) {
    // unarchive = set to draft
    this.handleUpdateStatus(
      params.allSelected,
      params.selectedRows,
      params.totalRecords,
      params.searchModel,
      params.action,
      params.searchService
    );
  }
  onProductsDeleteAll(params: any) {
    this.processService.deleteArchivedItems(
      this.brandId,
      [],
      encodeURIComponent(params.searchModel.searchValue.trim()),
      true
    )
      .pipe(takeWhile(() => this.isPageActive))
      .subscribe( (response: any) => {
        if (!response.processId) {
          this.runSearch(this.searchModel);
        } else {
          this.stopLoading();
          const process: QueueProcess = new QueueProcess(response.processId, 0);
          this.activeQueueProcesses.push(process);
          if (this.headerEl) {
            this.headerEl.onShowNotifications();
          }
          this.detectQueueProcessProgress(process);
        }
      }, error => { this.stopLoading(); } );
  }

  onScorecardChanged(scorecard: any) {
    if (scorecard && scorecard._id === 'off') {
      this.clearScorecardFilter();
    } else {
      this.setScorecardFilter(scorecard);
    }
    this.scorecardService.setSelectedScorecardId(scorecard._id);
    this.selectedScorecardId = scorecard._id;
    this._searchModel.next(this.searchModel);
  }

  onLeftTabClick() {
    if (this.isWorkingPublishedPage) {
      this.router.navigate(['/my-products/working']);
    } else {
      this.router.navigate(['/export-manager']);
    }
  }

  onRightTabClick() {
    if (this.isWorkingPublishedPage) {
      this.router.navigate(['/my-products/published']);
    } else {
      this.router.navigate(['/import-manager']);
    }
  }

  /*******************************************
   * COMPONENT PROTECTED METHODS
   *******************************************/

  protected addColumnsByRoute(route: string): void {
    // import-review pages dont't have column builder
    if (this.isImportReviewRoute(route)) {
      this.searchResultsColumns = _.cloneDeep(columns[route]);
      return;
    }

    // try to get columns from url query
    if (_.has(this.route.snapshot.queryParams, 'sc')) {
      try {
        this.searchResultsColumns = JSON.parse(this.route.snapshot.queryParams.sc);
        this.setColumnMissingData(route);
      } catch (e) {
        this.searchResultsColumns = _.cloneDeep(columns[route]);
      }
      return;
    }
    // when user route from dashboard scorcard
    // then we display columns from columnPresets Segemnt Scores
    if (_.get(this.route.snapshot.params, 'selectedScorecard')) {
      this.searchResultsColumns = this.getDefaultColumns(route);
      const preset: ColumnPreset = _.find(this.columnPresets, ['name', 'segmentScores']);
      const columnsData = _.map(preset.columnNames, (columnName: string) => {
        return _.find(availableColumns[route], ['option', columnName]);
      });
      this.searchResultsColumns = this.createNewColumns(columnsData);
      this.searchModel.columns = _.chain(this.searchResultsColumns).tail().map('columnName').value() as Array<string>;
      return;
    }
    // try get columns from localStorage or set default columns
    const storedColumns = this.getStoredColumns(route);
    if (storedColumns && storedColumns.length) {
      this.searchResultsColumns = this.getStoredColumns(route);
      this.setColumnMissingData(route);
    } else {
      this.searchResultsColumns = _.cloneDeep(columns[route]);
    }
  }

  /*******************************************
   * COMPONENT PRIVATE METHODS
   *******************************************/

  private subscribeRouterChange(): Subscription {
    return this.router.events
    .pipe(filter(event => event instanceof NavigationEnd))
    .subscribe((event: NavigationEnd) => {
      this.handleRouterChange(event);
    });
  }

  private handleRouterChange(event?: NavigationEnd): void {
    this.updateSaveSuffix();
    this.showEmptyResultsState = false;
    const route = this.page;

    // check importComplete periodically if it's 'import-review-aces'
    if (route === 'import-review-aces') {
      if (!this.checkImportCompleteTimeout) {
        this.checkImportComplete();
      }
    } else {
      this.clearCheckImportCompleteTimeout();
    }

    if (this.searchModel.route !== route ) {
      this.searchService.searchResults = [];
      this.searchService.selectAllChecked = [];
      this.searchService.selectedItems = [];
      this.searchService.allSelected = false;

      this.toggleAdvancedSearch(false);
    }

    this.userService.getUser()
      .subscribe( (user) => {
        this.user = user;
        this.updateSaveSuffix();
        if (this.storedSearchModel && !_.get(this.route.snapshot.queryParams, 'csm')) {
          this.searchModel = this.storedSearchModel;
        } else {
          this.searchModel = new SearchModel();
          this.searchModel.route = route;
        }
        this.resetSearchValue();
        this.handleSearchModelBeforeSearchSubj
          .pipe(takeWhile(() => this.isPageActive))
          .subscribe(() => {
            this._searchModel.next(this.searchModel);
          });
        this.handleSearchModelBeforeSearch();
      });

    this.setRowsHover(route);
    // set searchResultsColumns by the route
    this.addColumnsByRoute(route);
    if (!this.isImportReviewRoute(route)) {
      this.fieldNames = _.cloneDeep(availableColumns[route]);
    }
    // set actions visibility
    this.setRouteActions(route);
  }

  private setRowsHover(route: string): boolean {
    this.rowsHover = true;
    if (route === 'import-review-aces') {
      this.rowsHover = false;
    }
    return this.rowsHover;
  }

  private processStatusChange(action: any, itemIds: Array<Number>) {
    const interval = 500;
    let timer = 0;
    const chunk = 2;
    let i = 0;
    const statusRequests: Array<any> = [];
    const size = Math.ceil(itemIds.length / chunk);

    if (this.loader) {
      this.loader.show();
    }

    for (let j = 0; j < size; j++) {
      statusRequests.push({
        processId: j.toString(),
        count: 0,
        completed: false
      });
    }

    _.forEach(_.chunk(itemIds, chunk), (selectedItemIds) => {

      statusRequests[i].count = selectedItemIds.length;

      timer = interval * i++;

      _.delay((ids: Array<Number>, processId: string) => {
        const x = this.processService.postStatusUpdateItemsImmediate(action.action, this.brandId, ids, processId)
          .pipe(takeWhile(() => this.isPageActive))
          .subscribe( response => {
            const statusRequest = _.find(statusRequests, ['processId', response.processId]);

            if (statusRequest) {
              statusRequest.completed = true;
            }

            if (!_.find(statusRequests, ['completed', false])) {
                this.stopLoading();
                this.searchModel.selectAll = 'on-page';
                this.searchService.deselectAll();
                this.runSearch(this.searchModel);
            }

          }, error => {
            if (Config.BUGSNAG_KEY) {
              Bugsnag.notify(new Error(JSON.stringify(error)));
            }
          } );
        }, timer, selectedItemIds, statusRequests[i - 1].processId);
      });
  }

  private handleUpdateStatus(
    allSelected: boolean, selectedRows: Array<any>, totalRecords: number, searchModel: SearchModel, action: any, searchService: any
  ) {
    const targetSearchService = searchService || this.searchService;
    if (searchService) { // if called from archive modal
      this.processService.postStatusUpdateAllItemsImmediate(
        action.action,
        this.brandId,
        _.map(selectedRows, 'itemId'),
        encodeURIComponent(searchModel.searchValue.trim()),
        allSelected
      )
        .pipe(takeWhile(() => this.isPageActive))
        .subscribe( (response: any) => {
          if (!response.processId) {
            this.runSearch(this.searchModel);
          } else {
            this.stopLoading();
            const process: QueueProcess = new QueueProcess(response.processId, 0);
            this.activeQueueProcesses.push(process);
            if (this.headerEl) {
              this.headerEl.onShowNotifications();
            }
            this.detectQueueProcessProgress(process);
          }
        }, error => { this.stopLoading(); } );
      return;
    }
    if ((!allSelected && selectedRows.length <= 100) || (allSelected && totalRecords <= 100)) {

      let itemIds: Array<Number> = [];

      if (allSelected)  {
        itemIds = [];
        targetSearchService.getSearchIds(this.brandId, searchModel)
          .pipe(takeWhile(() => this.isPageActive))
          .subscribe((ids: any) => {
            // request failed (handleError returns an error string) — do not post garbage ids
            if (!_.isArray(ids) || !ids.length) {
              this.stopLoading();
              return;
            }
            this.processStatusChange(action, ids);
          });
      } else {
        itemIds = _.map(selectedRows, this.searchService.idField);
        this.processStatusChange(action, itemIds);
      }
    } else {
      let request: any;
      if (allSelected) {
        const _filter = targetSearchService.getQueryString(this.searchModel, true);
        request = this.processService.postStatusUpdateItems(action.action, this.brandId, [], _filter);
      } else {
        request = this.processService.postStatusUpdateItems(action.action, this.brandId, _.map(selectedRows, 'itemId'));
      }

      request
      .pipe(takeWhile(() => this.isPageActive))
      .subscribe( (response: any) => {
        if (!response.processId) {
          this.runSearch(this.searchModel);
        } else {
          this.stopLoading();
          const process: QueueProcess = new QueueProcess(response.processId, 0);
          this.activeQueueProcesses.push(process);
          if (this.headerEl) {
            this.headerEl.onShowNotifications();
          }
          this.detectQueueProcessProgress(process);
        }
      }, error => { this.stopLoading(); } );
    }
  }

  private getStoredColumns(route: string): Array<any> | null {
    if (_.has(this, 'user.id')) {

      const linkEmptyUnderlineOnHoverVarName = `newColumnsProcess_linkEmptyUnderlineOnHover.${route}.${this.brandId}.${this.user.id}`;
      const newColumnsGhostValueVarName = `newColumnsProcess_newColumnsGhostValue.${route}.${this.brandId}.${this.user.id}`;
      const newColumnsFitmentsSortableVarName = `newColumnsProcess_fitmentsSortable.${route}.${this.brandId}.${this.user.id}`;
      const newColumnsScorecards = `newColumnsProcess_scorecards.${route}.${this.brandId}.${this.user.id}`;
      // remove linkEmptyUnderlineOnHover (https://speedlab.atlassian.net/browse/SPD2-2919) feature variable
      if (localStorage.getItem(linkEmptyUnderlineOnHoverVarName)) {
        localStorage.removeItem(linkEmptyUnderlineOnHoverVarName);
      }
      // remove new ghostValue of columns feature variable
      if (localStorage.getItem(newColumnsGhostValueVarName)) {
        localStorage.removeItem(newColumnsGhostValueVarName);
      }
      // remove new ghostValue of columns feature variable for this https://speedlab.atlassian.net/browse/SPD2-3275
      if (localStorage.getItem(newColumnsFitmentsSortableVarName)) {
        localStorage.removeItem(newColumnsFitmentsSortableVarName);
      }
      // remove new ghostValue of columns feature variable for this https://speedlab.atlassian.net/browse/SPD2-3275
      if (localStorage.getItem(newColumnsScorecards)) {
        localStorage.removeItem(newColumnsScorecards);
      }

      // if it's first load after adding scorecards - don't use stored columns
      const varName = `newColumnsProcess_scorecards_bugfix.${route}.${this.brandId}.${this.user.id}`;
      if (!localStorage.getItem(varName)) {
        localStorage.setItem(varName, 'true');
        return null;
      }
      return JSON.parse(localStorage.getItem(`myProductsColumns.${this.saveSuffix}`));
    } else {
      return null;
    }
  }

  private setColumnMissingData(route: string): void {
    _.each(_.drop(this.searchResultsColumns), (item: any) => {
      const col = _.find(availableColumns[route], ['column_name', item.columnName]);
      if (col) {
        item.data = col.data;
        if (col.link) {
          item.link = col.link;
        }
      }
    });
  }

  private navigateToDetailsPage(item: any, segmentName?: string) {
    let segment = 'overview';
    if (segmentName) {
      segment = segmentName;
    }
    if (item) {
      this.returnRouteService.push(new ReturnRouteModel(`/${this.route.snapshot.url.join('/')}`, {smlocal: 1}));
      this.router.navigate([
        `/${this.detailsPageRootRoute}`,
        item.itemId || 1,
        segment,
      ]);
    }
  }

  private  getSearchId(): any {
    const routeSearchId = this.route.snapshot.params['searchId'];
    return routeSearchId ? routeSearchId : null;
  }

  private clearCheckImportCompleteTimeout() {
    if (this.checkImportCompleteTimeout) {
      clearTimeout(this.checkImportCompleteTimeout);
    }
    this.checkImportCompleteTimeout = null;
  }

  private checkImportComplete() {
    this.searchService.runSearch(this.brandId, this.searchModel, false)
    .pipe(takeWhile(() => this.isPageActive))
    .subscribe((data: any) => {
      this.importComplete = data.importComplete;
      this.ref.markForCheck();
      if (this.importComplete) {
        this.clearCheckImportCompleteTimeout();
      } else {
        // start next only when current request completed
        this.checkImportCompleteTimeout = setTimeout(() => {
          this.checkImportComplete();
        }, 10000);
      }
    });
  }

  private setRouteActions(route: string) {
    if (!this.isImportReviewRoute(route)) {
      this.actions = actions;
    } else {
      this.actions = [];
    }
    _.each(this.actions, (item: any) => {
      item.hidden = !_.includes(item.availableOn, route);
    });
  }

  private exportItems(exportConfig: any, isToReceiver?: boolean) {
    if (this.allSelected) {

      const _filter = this.searchService.getQueryString(this.searchModel, true);

      if (isToReceiver) {
        this.postExportReceiverItems([], status, _filter, exportConfig);
      } else {
        this.postExportItems([], status, _filter, exportConfig);
      }
    } else {
      const ids = _.map(this.selectedRows, 'itemId');
      const scorecardId = this.getScorecardId();
      let _filter = '';
      if (scorecardId) {
          _filter = 'filter[scorecardId]=' + encodeURIComponent(scorecardId);
      }

      if (isToReceiver) {
        this.postExportReceiverItems(ids, status, null, exportConfig);
      } else {
        this.postExportItems(ids, status, _filter, exportConfig);
      }
    }
  }

  private postExportItems(ids: string[], status: string, _filter: any, exportConfig: any) {

    const _columns: any = _.map(_.filter(this.searchResultsColumns, (column: any) => {
      return !(column.title === 'select-all-btn' || column.title === '');
    }), (column: any) => {

      const field_name = _.get(_.find(availableColumns.working, {column_name: column.columnName}), 'field_name');
      return { title: column.title, columnName: field_name };
    });

    const runNow = _.get(exportConfig, 'runNow', false);

    this.processService.postExportItems(
      this.brandId,
      exportConfig.exportTypeIds,
      ids,
      status,
      _filter,
      exportConfig.priceSheets,
      exportConfig.priceSheetCurrencies,
      exportConfig.userFileName,
      this.currentRoute,
      exportConfig.customFields,
      _columns,
      runNow,
      exportConfig.schedule,
      exportConfig.scheduleName,
    )
      .subscribe(
        response => {
          this.stopLoading();
          if (exportConfig.schedule && !runNow) {
            this.toastrService.info('', '', exportScheduledSuccessfullyToast());
          } else {
            if (this.configureExportWidget) {
              this.configureExportWidget.showFinalExportModal();
            }
          }
        },
        error => { this.stopLoading(); }
      );
  }

  private postExportReceiverItems(ids: string[], status: string, _filter: any, exportConfig: any) {

    const runNow = _.get(exportConfig, 'runNow', false);

    this.processService.postExportReceiverItems(
      this.brandId,
      ids,
      status,
      _filter,
      exportConfig.priceSheets,
      exportConfig.priceSheetCurrencies,
      exportConfig.receivers,
      exportConfig.userFileName,
      this.currentRoute,
      exportConfig.customFields,
      runNow,
      exportConfig.schedule,
      exportConfig.scheduleName,
    )
      .subscribe(
        response => {
          this.stopLoading();
          if (exportConfig.schedule && !runNow) {
            this.toastrService.info('', '', exportScheduledSuccessfullyToast());
          } else {
            if (this.configureExportWidget) {
              this.configureExportWidget.showFinalExportModal();
            }
          }
        },
        error => { this.stopLoading(); }
      );
  }

  private postExportItemsValid() {

    let _filter = '';
    let ids = [];
    if (this.allSelected) {
        _filter = this.searchService.getQueryString(this.searchModel, true);
    } else {
      ids = _.map(this.selectedRows, 'itemId');
    }
    this.processService.postExportItemsValid(
      this.brandId, ids, status, _filter, this.currentRoute,
    )
      .subscribe(
        response => {
          this.stopLoading();
          const pies65Total = _.get(response, 'invalid-pies-65', 0);
          const pies67Total = _.get(response, 'invalid-pies-67', 0);
          const pies70Total = _.get(response, 'invalid-pies-70', 0);

          if (pies65Total) {
            console.log('PIES 65 invalid', pies65Total);
          }
          if (pies67Total) {
            console.log('PIES 67 invalid', pies67Total);
          }
          if (pies70Total) {
            console.log('PIES 70 invalid', pies70Total);
          }

          if (pies70Total) {
            const note = 'Validation errors blocked your export to receivers. Ensure PIES validation scorecards are 100% before exporting.';
            this.appModalService.showModal(
              SIMPLE_CONFIRM_MODAL_NAME,
              {
                secondaryBtnTitle: 'OK',
                note: note,
                title: 'Export Blocked',
                version2: true,
              },
            );
          } else {
            if (this.configureExportWidget) {
              this.configureExportWidget.configureReceiverExport();
            }
          }
        },
        error => { this.stopLoading(); }
      );
  }

  private postItemsValid(action: any) {

    let _filter = '';
    let ids = [];
    if (this.allSelected) {
        _filter = this.searchService.getQueryString(this.searchModel, true);
    } else {
      ids = _.map(this.selectedRows, 'itemId');
    }
    if (this.loader) {
      this.loader.show();
    }
    this.processService.postItemsValid(
      this.brandId, ids, status, _filter, this.currentRoute,
    )
      .subscribe(
        response => {
          this.stopLoading();
          const pies65Total = _.get(response, 'invalid-pies-65', 0);
          const pies67Total = _.get(response, 'invalid-pies-67', 0);

          if (pies65Total) {
            console.log('PIES 65 invalid', pies65Total);
          }
          if (pies67Total) {
            console.log('PIES 67 invalid', pies67Total);
          }

          if (pies65Total || pies67Total) {
            if (this.publishWarningModal) {
              this.publishWarningModal.data = action;
              this.publishWarningModal.show();
            }
          } else {
              super.onActionSelected(action);
          }
        },
        error => { this.stopLoading(); }
      );
  }

  onPublishAnyway(action: any) {
      super.onActionSelected(action);
  }

  private createScorecardFilter(scorecard: any) {
    return new ActiveFilterModel(
      'scorecardId',
      'scorecardId',
      SIMPLE_FILTER_TYPE,
      scorecard._id,
      null,
    );
  }

  private setScorecardFilter(scorecard: any) {
    const _filter: any = this.createScorecardFilter(scorecard);
    // @ts-ignore
      const currFilterIndex = _.findIndex(this.searchModel.filters, {_id: 'scorecardId'});

    if (currFilterIndex >= 0) {
      this.searchModel.filters.splice(currFilterIndex, 1, _filter);
    } else {
      this.searchModel.filters.push(_filter);
    }
  }

  private getScorecardId() {
    // @ts-ignore
      const currFilterIndex = _.findIndex(this.searchModel.filters, {_id: 'scorecardId'});

    if (currFilterIndex >= 0) {
      return this.searchModel.filters[currFilterIndex].data;
    } else {
      return null;
    }
  }

  private clearScorecardFilter() {
    // @ts-ignore
      const currFilterIndex = _.findIndex(this.searchModel.filters, {_id: 'scorecardId'});

    if (currFilterIndex >= 0) {
      this.searchModel.filters.splice(currFilterIndex, 1);
    }
  }

  handleCompleteQueueProcess(queueProcess: QueueProcess): void {
    super.handleCompleteQueueProcess(queueProcess);
    this.alertsService.processingComplete.emit(queueProcess);
    // or show processing complete toast
    // this.toastrService.info('', '', processingCompleteToast());
  }

  private isImportReviewRoute(route: string): boolean {
    return _.includes(this.importReviewRoutes, route);
  }
}
